/**
 * PA Operations Control Tower v2 — companion tabs.
 *
 * One file, nine tabs. Each tab is a small lazy-fetched component that
 * reads from /api/pa/v2/* and renders a flat, operator-heavy view —
 * NOT a chat UI.
 *
 * Used by: pages/PAOperationsPage.js (imported + dropped under <Tabs>)
 */
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle, Ban, Bot, ClipboardList, Cog, FileSearch, Gauge,
  HardDrive, Inbox, Loader2, MessageSquareWarning, Play, Power, RotateCcw,
  Search, ShieldOff, Sparkles, Trash2, TrendingUp, Workflow, XCircle, Send,
  Eye, FileText, CheckCircle2, Paperclip, HelpCircle,
} from "lucide-react";
import { API_URL } from "../config/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";

/* ── shared helpers ──────────────────────────────────────────────────── */

const api = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const usePAv2 = (path, deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API_URL}/pa/v2${path}`, api());
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { reload(); }, [reload]);
  return { data, loading, error, reload };
};

const EmptyState = ({ icon: Icon, title, sub }) => (
  <div className="text-center py-16 text-slate-400" data-testid="pa-v2-empty">
    <Icon className="h-10 w-10 mx-auto mb-2 opacity-40" />
    <p className="text-sm font-medium">{title}</p>
    {sub && <p className="text-xs mt-1 max-w-md mx-auto leading-relaxed">{sub}</p>}
  </div>
);

const Loading = () => (
  <div className="flex items-center justify-center py-12 text-slate-400">
    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
  </div>
);

const KPI = ({ label, value, accent = "slate", testId }) => (
  <div className={`bg-white rounded-lg border px-3 py-2 border-${accent}-100`}
       data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`text-xl font-bold text-${accent}-700`}>{value ?? "—"}</p>
  </div>
);

const fmtTs = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("sl-SI"); } catch { return iso; }
};

/* ═════════════════════════════════════════════════════════════════════
 * TAB 1: Unsafe Replies Queue
 * ═══════════════════════════════════════════════════════════════════ */
export function UnsafeRepliesTab() {
  const [filter, setFilter] = useState("");
  const { data, loading, reload } = usePAv2("/unsafe-replies?limit=100", []);
  const items = (data?.items || []).filter(r =>
    !filter || JSON.stringify(r).toLowerCase().includes(filter.toLowerCase()));

  return (
    <Card data-testid="pa-v2-unsafe-tab">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldOff className="h-4 w-4 text-red-600" />
          Unsafe Replies ({data?.total || 0})
        </CardTitle>
        <div className="flex items-center gap-2">
          <Input value={filter} onChange={e => setFilter(e.target.value)}
                 placeholder="Filter…" className="h-8 text-xs w-48"
                 data-testid="unsafe-filter" />
          <Button size="sm" variant="ghost" onClick={reload}>
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? <Loading /> : items.length === 0 ? (
          <EmptyState icon={ShieldOff} title="No unsafe replies blocked"
            sub="When the validator blocks a draft, it appears here for audit." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="unsafe-table">
              <thead className="text-left text-xs text-slate-500 border-b">
                <tr><th className="pb-2 pr-2">Time</th><th className="pb-2 pr-2">Sender</th>
                <th className="pb-2 pr-2">Category</th><th className="pb-2 pr-2">Blocked phrase</th>
                <th className="pb-2 pr-2">Reason</th><th className="pb-2">Fallback</th></tr>
              </thead>
              <tbody>
                {items.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-1.5 pr-2 text-xs text-slate-500">{fmtTs(r.created_at)}</td>
                    <td className="py-1.5 pr-2 text-xs truncate max-w-[200px]">{r.from_email || r.sender}</td>
                    <td className="py-1.5 pr-2"><span className="text-xs px-1.5 py-0.5 bg-slate-100 rounded">{r.category || "—"}</span></td>
                    <td className="py-1.5 pr-2 text-xs font-mono text-red-700 max-w-[240px] truncate">{r.blocked_phrase}</td>
                    <td className="py-1.5 pr-2 text-xs text-slate-600">{r.validator_reason}</td>
                    <td className="py-1.5 text-xs">{r.fallback_action || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * TAB 2: REPORT Corrections
 * ═══════════════════════════════════════════════════════════════════ */
export function CorrectionsTab() {
  const [filter, setFilter] = useState("open");
  const { data, loading, reload } = usePAv2(
    `/corrections${filter === "open" ? "?status=open" : ""}`, [filter]);

  const resolve = async (id) => {
    try {
      await axios.post(`${API_URL}/pa/v2/corrections/${id}/resolve`,
        { resolution_note: "Resolved from control tower" }, api());
      toast.success("Correction resolved");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Card data-testid="pa-v2-corrections-tab">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <MessageSquareWarning className="h-4 w-4 text-amber-600" />
          REPORT Corrections (open: {data?.open || 0})
        </CardTitle>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={e => setFilter(e.target.value)}
                  className="h-8 text-xs border rounded px-2">
            <option value="open">Open</option>
            <option value="all">All</option>
            <option value="resolved">Resolved</option>
          </select>
          <Button size="sm" variant="ghost" onClick={reload}><RotateCcw className="h-3.5 w-3.5" /></Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? <Loading /> : (data?.items || []).length === 0 ? (
          <EmptyState icon={MessageSquareWarning} title="No corrections"
            sub="When customers REPORT a bad reply, the original email + corrected operator response land here." />
        ) : (
          <div className="space-y-3">
            {data.items.map(c => (
              <div key={c.id} className="border rounded-lg p-3 bg-white"
                   data-testid={`correction-${c.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-slate-400">{c.id}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.status === "resolved" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                      {c.status || "open"}
                    </span>
                    <span className="text-[10px] text-slate-400">{fmtTs(c.created_at)}</span>
                  </div>
                  {c.status !== "resolved" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => resolve(c.id)}>Resolve</Button>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">Original email →</p>
                <p className="text-sm">{c.original_subject || c.original_email?.subject}</p>
                <p className="text-xs text-slate-500 mt-2">Bot replied →</p>
                <p className="text-sm italic bg-slate-50 rounded p-2">{(c.bot_reply || "—").slice(0, 300)}</p>
                <p className="text-xs text-red-600 mt-2">Customer reported →</p>
                <p className="text-sm bg-red-50 rounded p-2">{c.report_reason}</p>
                {c.corrected_operator_response && (<>
                  <p className="text-xs text-green-700 mt-2">Operator corrected →</p>
                  <p className="text-sm bg-green-50 rounded p-2">{c.corrected_operator_response}</p>
                </>)}
                {c.routing_failure && <p className="text-[10px] text-slate-400 mt-2">Routing failure: <span className="font-mono">{c.routing_failure}</span></p>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * TAB 3: Failed Jobs / Dead Letter
 * ═══════════════════════════════════════════════════════════════════ */
export function FailedJobsTab() {
  const { data, loading, reload } = usePAv2("/failed-jobs?limit=100", []);

  const action = async (id, kind) => {
    try {
      const url = `${API_URL}/pa/v2/failed-jobs/${id}/${kind}`;
      await axios.post(url, kind === "discard" ? { reason: "operator decision" } : {}, api());
      toast.success(kind === "replay" ? "Queued for replay (sandboxed — needs approval)" : "Discarded");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Card data-testid="pa-v2-failed-tab">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <XCircle className="h-4 w-4 text-red-600" />
          Failed Jobs / Dead Letter ({data?.total || 0})
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={reload}><RotateCcw className="h-3.5 w-3.5" /></Button>
      </CardHeader>
      <CardContent>
        {loading ? <Loading /> : (data?.items || []).length === 0 ? (
          <EmptyState icon={XCircle} title="No failed jobs"
            sub="When MyClaw fails to process an email after retries, the job lands here for manual review." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="failed-table">
              <thead className="text-left text-xs text-slate-500 border-b">
                <tr><th className="pb-2 pr-2">Time</th><th className="pb-2 pr-2">Message ID</th>
                <th className="pb-2 pr-2">Stage</th><th className="pb-2 pr-2">Retries</th>
                <th className="pb-2 pr-2">Exception</th><th className="pb-2">Actions</th></tr>
              </thead>
              <tbody>
                {data.items.map(j => (
                  <tr key={j.id} className="border-b border-slate-50 hover:bg-slate-50"
                      data-testid={`failed-row-${j.id}`}>
                    <td className="py-1.5 pr-2 text-xs text-slate-500">{fmtTs(j.created_at)}</td>
                    <td className="py-1.5 pr-2 text-xs font-mono truncate max-w-[180px]">{j.message_id || j.email_id}</td>
                    <td className="py-1.5 pr-2"><span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-mono">{j.failed_stage}</span></td>
                    <td className="py-1.5 pr-2 text-xs">{j.retry_count ?? 0}</td>
                    <td className="py-1.5 pr-2 text-xs text-red-700 font-mono max-w-[260px] truncate">{j.exception_summary || j.exception}</td>
                    <td className="py-1.5 text-xs flex gap-1">
                      <Button size="sm" variant="outline" className="h-6 text-[11px] px-2"
                              onClick={() => action(j.id, "replay")}
                              data-testid={`failed-replay-${j.id}`}>
                        <Play className="h-3 w-3 mr-1" /> Replay
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 text-[11px] px-2 text-slate-500"
                              onClick={() => action(j.id, "discard")}>
                        <Trash2 className="h-3 w-3 mr-1" /> Discard
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * TAB 4: Replay Queue
 * ═══════════════════════════════════════════════════════════════════ */
export function ReplayQueueTab() {
  const { data, loading, reload } = usePAv2("/replay-queue?limit=100", []);
  const [diffFor, setDiffFor] = useState(null);

  const approve = async (id, mode) => {
    try {
      await axios.post(`${API_URL}/pa/v2/replay-queue/${id}/approve`,
        { mode, auto_send: false }, api());
      toast.success(`Replay ${id} approved in ${mode} mode (sandboxed)`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Card data-testid="pa-v2-replay-tab">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Workflow className="h-4 w-4 text-violet-600" />
          Replay Queue · pending: {data?.pending || 0} · running: {data?.running || 0} · completed: {data?.completed || 0}
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={reload}><RotateCcw className="h-3.5 w-3.5" /></Button>
      </CardHeader>
      <CardContent>
        <div className="bg-violet-50 border border-violet-200 rounded p-2 mb-3 text-xs text-violet-800">
          <b>Sandboxed by default.</b> Replay NEVER auto-sends, NEVER modifies
          ERP, NEVER triggers customer notifications. Operator approves a mode
          (dry-run / template-only / no-AI / full); MyClaw runs it and writes
          the result here for side-by-side review.
        </div>
        {loading ? <Loading /> : (data?.items || []).length === 0 ? (
          <EmptyState icon={Workflow} title="No replays queued"
            sub="Replays are created from the Failed Jobs tab." />
        ) : (
          <div className="space-y-2">
            {data.items.map(r => (
              <div key={r.id} className="border rounded p-2.5 bg-white flex items-center gap-3"
                   data-testid={`replay-row-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-slate-400">{r.id}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      r.status === "pending" ? "bg-amber-100 text-amber-700" :
                      r.status === "running" ? "bg-blue-100 text-blue-700" :
                      r.status === "completed" ? "bg-green-100 text-green-700" :
                      r.status === "failed" ? "bg-red-100 text-red-700" :
                      "bg-slate-100 text-slate-600"}`}>{r.status}</span>
                    <span className="text-[10px] text-slate-400">mode: {r.mode}</span>
                    {r.auto_send && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">auto-send</span>}
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5">
                    source: <span className="font-mono">{r.source_ticket || r.source_job_id}</span>
                    {r.message_id && <> · msg: <span className="font-mono">{r.message_id}</span></>}
                  </p>
                  {r.diff_summary && <p className="text-[11px] text-slate-500 italic mt-0.5">{r.diff_summary}</p>}
                </div>
                <div className="flex gap-1 flex-wrap justify-end">
                  {r.status === "completed" && (
                    <Button size="sm" variant="default" className="h-6 text-[10px] px-2 bg-violet-600 hover:bg-violet-700"
                            onClick={() => setDiffFor(r.id)}
                            data-testid={`replay-diff-${r.id}`}>
                      View diff
                    </Button>
                  )}
                  {r.status === "pending" && ["dry_run", "template_only", "no_ai", "full"].map(m => (
                    <Button key={m} size="sm" variant="outline"
                            className="h-6 text-[10px] px-2"
                            onClick={() => approve(r.id, m)}
                            data-testid={`replay-${m}-${r.id}`}>
                      {m.replace("_", " ")}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      {diffFor && <ReplayDiffModal replayId={diffFor} onClose={() => setDiffFor(null)}
                                     onPromoted={() => reload()} />}
    </Card>
  );
}

/* ───── ReplayDiffModal — side-by-side comparison view (operator-approved) ───── */
function ReplayDiffModal({ replayId, onClose, onPromoted }) {
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoteOpen, setPromoteOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/pa/v2/replay-queue/${replayId}/diff`, api())
      .then(r => { if (alive) setDiff(r.data); })
      .catch(() => { if (alive) setDiff({ error: "Failed to load diff" }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [replayId]);

  const fmtVal = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  // Role-gate: only admin / supervisor can promote.
  const me = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; }
  })();
  const canPromote = ["admin", "supervisor"].includes((me.role || "").toLowerCase());

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto"
         data-testid="replay-diff-modal" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mt-12 mb-12" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Workflow className="h-4 w-4 text-violet-600" />
            <h3 className="font-semibold text-sm">Replay diff · <span className="font-mono text-violet-700">{replayId}</span></h3>
            {diff && diff.change_count > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                {diff.change_count} change{diff.change_count !== 1 ? "s" : ""}
              </span>
            )}
            {diff && diff.change_count === 0 && (
              <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">No change</span>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <XCircle className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {loading ? <Loading /> :
           diff?.error ? <EmptyState icon={XCircle} title={diff.error} /> :
           !diff ? null : (
            <>
              <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                <div className="bg-slate-50 rounded p-2"><b>Source ticket:</b> <span className="font-mono">{diff.source_ticket || "—"}</span></div>
                <div className="bg-slate-50 rounded p-2"><b>Mode:</b> {diff.mode} · auto-send: <span className={diff.auto_send ? "text-red-600 font-bold" : "text-emerald-700"}>{String(diff.auto_send)}</span></div>
              </div>
              <table className="w-full text-sm border-collapse" data-testid="diff-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1.5 pr-3 text-xs text-slate-500 uppercase tracking-wider w-32">Field</th>
                    <th className="text-left py-1.5 pr-3 text-xs text-slate-500 uppercase tracking-wider">Original result</th>
                    <th className="text-left py-1.5 text-xs text-slate-500 uppercase tracking-wider">Replay result</th>
                  </tr>
                </thead>
                <tbody>
                  {diff.rows.map(row => (
                    <tr key={row.key} className={`border-b border-slate-50 ${row.changed ? "bg-amber-50" : ""}`}>
                      <td className="py-1.5 pr-3 text-xs font-medium text-slate-700 align-top">{row.label}</td>
                      <td className="py-1.5 pr-3 text-xs align-top">
                        <pre className={`whitespace-pre-wrap font-mono ${row.changed ? "text-slate-700" : "text-slate-500"}`}>{fmtVal(row.original)}</pre>
                      </td>
                      <td className="py-1.5 text-xs align-top">
                        <pre className={`whitespace-pre-wrap font-mono ${row.changed ? "text-emerald-700 font-semibold" : "text-slate-500"}`}>{fmtVal(row.replay)}</pre>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {diff.diff_summary && (
                <p className="mt-3 text-xs text-slate-500 italic">{diff.diff_summary}</p>
              )}
              <p className="mt-3 text-[11px] text-slate-400">
                <b>Reminder:</b> this is a sandboxed result. Nothing was sent, no ERP records touched, no customer notified.
              </p>

              {/* Promote-to-production CTA — role-gated + only when changes exist */}
              {diff.change_count > 0 && (
                <div className="mt-4 border-t pt-3 flex items-center justify-between gap-3">
                  <div className="text-xs text-slate-600 max-w-md">
                    Operationally better? Promote this routing/template change. Emergent
                    writes a <code className="bg-slate-100 px-1 rounded">routing_change_log</code> row;
                    MyClaw polls, validates, hot-reloads safely. Always reversible.
                  </div>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                          disabled={!canPromote}
                          title={canPromote ? "" : "Requires admin / supervisor role"}
                          onClick={() => setPromoteOpen(true)}
                          data-testid="promote-replay-btn">
                    Promote to Production →
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {promoteOpen && (
        <PromotePreviewModal
          replayId={replayId} diff={diff}
          operatorEmail={me.email || ""}
          onClose={() => setPromoteOpen(false)}
          onPromoted={(changeId) => {
            setPromoteOpen(false);
            onClose();
            onPromoted && onPromoted(changeId);
          }} />
      )}
    </div>
  );
}

/* ───── PromotePreviewModal — rule delta + impact + commit ───── */
const CHANGE_TYPES = [
  { v: "keyword_override",      label: "Keyword override",      hint: "Add/remove keywords on a router" },
  { v: "template_variant",      label: "Template variant",      hint: "Swap template version for a category" },
  { v: "validator_phrase_list", label: "Validator phrase list", hint: "Append/remove validator phrases" },
  { v: "routing_rule",          label: "Routing rule",          hint: "Versioned routing rule update" },
  { v: "rfq_classification",    label: "RFQ classification",    hint: "RFQ pattern adjustment" },
];

function PromotePreviewModal({ replayId, diff, operatorEmail, onClose, onPromoted }) {
  const [changeType, setChangeType] = useState("keyword_override");
  const [targetModule, setTargetModule] = useState("rfq_routing");
  const [reason, setReason] = useState("");
  const [version, setVersion] = useState("");
  const [committing, setCommitting] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 5) {
      toast.error("Approval reason needed (min 5 chars — operational audit).");
      return;
    }
    setCommitting(true);
    try {
      const r = await axios.post(
        `${API_URL}/pa/v2/replay-queue/${replayId}/promote`,
        {
          change_type: changeType,
          target_module: targetModule,
          approval_reason: reason.trim(),
          approved_by: operatorEmail || "operator",
          version: version || undefined,
        }, api());
      toast.success(`Change ${r.data.change_id} approved · MyClaw will hot-reload on next poll.`);
      onPromoted(r.data.change_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    } finally { setCommitting(false); }
  };

  const changed = (diff?.rows || []).filter(r => r.changed);

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full" onClick={e => e.stopPropagation()}
           data-testid="promote-preview-modal">
        <div className="flex items-center justify-between border-b px-4 py-2.5">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            Promote replay → Production
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <XCircle className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {/* Rule delta */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-medium">Rule delta ({changed.length})</p>
            <div className="bg-slate-50 rounded p-2 text-xs max-h-32 overflow-y-auto">
              {changed.length === 0 ? <span className="text-slate-400">No changes</span> :
               changed.map(r => (
                 <div key={r.key} className="font-mono">
                   <span className="text-slate-500">{r.label}:</span>{" "}
                   <span className="text-red-600">{String(r.original ?? "—").slice(0,40)}</span>{" → "}
                   <span className="text-emerald-700 font-semibold">{String(r.replay ?? "—").slice(0,40)}</span>
                 </div>
               ))}
            </div>
          </div>

          {/* Change type — strict allow-list */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-medium">Change type</p>
            <select value={changeType} onChange={e => setChangeType(e.target.value)}
                    className="w-full h-8 text-xs border rounded px-2"
                    data-testid="promote-change-type">
              {CHANGE_TYPES.map(t => (
                <option key={t.v} value={t.v}>{t.label} — {t.hint}</option>
              ))}
            </select>
            <p className="text-[10px] text-slate-400 mt-1">
              ERP / financial / auth / bank / security / escalation_policy / retry_logic are <b>not promotable</b> — go through manual deployment review.
            </p>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-medium">Target module</p>
            <Input value={targetModule} onChange={e => setTargetModule(e.target.value)}
                   placeholder="e.g. rfq_routing, validator, pa_drafts"
                   className="h-8 text-xs" data-testid="promote-target-module" />
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-medium">Approval reason <span className="text-red-500">*</span></p>
            <textarea value={reason} onChange={e => setReason(e.target.value)}
                      rows={2} placeholder="Why is this an improvement? (min 5 chars — recorded in audit)"
                      className="w-full text-xs border rounded p-2"
                      data-testid="promote-reason" />
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-medium">Version tag (optional)</p>
            <Input value={version} onChange={e => setVersion(e.target.value)}
                   placeholder="e.g. v2.3.1" className="h-8 text-xs" />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-800">
            <b>Estimated impact:</b> the change applies to <code>{targetModule}</code>.
            MyClaw takes a rollback snapshot before applying, fails safe if reload errors,
            and you can revert anytime via the Changes tab.
          </div>
        </div>
        <div className="border-t px-4 py-2.5 flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={submit} disabled={committing}
                  data-testid="promote-commit-btn">
            {committing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Confirm & write change_log
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * TAB 10: Routing Changes (history + rollback)
 * ═══════════════════════════════════════════════════════════════════ */
export function RoutingChangesTab() {
  const { data, loading, reload } = usePAv2("/routing-changes?limit=100", []);
  const [rollbackFor, setRollbackFor] = useState(null);

  const me = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } })();
  const canRollback = ["admin", "supervisor"].includes((me.role || "").toLowerCase());

  return (
    <Card data-testid="pa-v2-changes-tab">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-emerald-600" />
          Routing Changes · approved: {data?.approved || 0} · activated: {data?.activated || 0} · failed: {data?.failed || 0}
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={reload}><RotateCcw className="h-3.5 w-3.5" /></Button>
      </CardHeader>
      <CardContent>
        <div className="bg-emerald-50 border border-emerald-200 rounded p-2 mb-3 text-xs text-emerald-800">
          <b>Operational learning, human-controlled.</b> Each row was promoted from a replay
          by an operator. MyClaw polls this log, validates, and hot-reloads safely. ERP /
          auth / bank / security changes are NOT promotable.
        </div>
        {loading ? <Loading /> : (data?.items || []).length === 0 ? (
          <EmptyState icon={TrendingUp} title="No routing changes yet"
            sub="When you promote a successful replay, the change record lands here." />
        ) : (
          <div className="space-y-2">
            {data.items.map(c => (
              <div key={c.change_id} className="border rounded p-2.5 bg-white"
                   data-testid={`change-row-${c.change_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-emerald-700">{c.change_id}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      c.status === "approved" ? "bg-amber-100 text-amber-700" :
                      c.status === "activated" ? "bg-emerald-100 text-emerald-700" :
                      c.status === "failed" ? "bg-red-100 text-red-700" :
                      c.status === "rolled_back" ? "bg-slate-200 text-slate-600" :
                      "bg-slate-100 text-slate-600"}`}>{c.status}</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded font-mono">{c.change_type}</span>
                    <span className="text-[10px] text-slate-500">→ {c.target_module}</span>
                    {c.version && <span className="text-[10px] text-slate-400">{c.version}</span>}
                  </div>
                  {c.status === "activated" && canRollback && (
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 text-red-600 border-red-200"
                            onClick={() => setRollbackFor(c)}
                            data-testid={`rollback-${c.change_id}`}>
                      Rollback
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div className="bg-red-50 rounded p-1.5">
                    <p className="text-[9px] uppercase text-red-600 mb-0.5">Before</p>
                    <pre className="text-[10px] whitespace-pre-wrap font-mono">{JSON.stringify(c.before, null, 2)}</pre>
                  </div>
                  <div className="bg-emerald-50 rounded p-1.5">
                    <p className="text-[9px] uppercase text-emerald-700 mb-0.5">After</p>
                    <pre className="text-[10px] whitespace-pre-wrap font-mono">{JSON.stringify(c.after, null, 2)}</pre>
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 mt-1 italic">{c.approval_reason}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Approved by <b>{c.approved_by}</b> · {fmtTs(c.created_at)}
                  {c.activated_at && <> · activated {fmtTs(c.activated_at)}</>}
                  {c.rolled_back_at && <> · rolled back by <b>{c.rolled_back_by}</b> {fmtTs(c.rolled_back_at)}: <i>{c.rolled_back_reason}</i></>}
                </p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      {rollbackFor && <RollbackModal change={rollbackFor} operator={me.email || ""}
                                      onClose={() => setRollbackFor(null)}
                                      onDone={() => { setRollbackFor(null); reload(); }} />}
    </Card>
  );
}

function RollbackModal({ change, operator, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 5) {
      toast.error("Rollback reason needed (min 5 chars)");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API_URL}/pa/v2/routing-changes/${change.change_id}/rollback`,
        { reason: reason.trim(), rolled_back_by: operator || "operator" }, api());
      toast.success(`${change.change_id} rolled back — MyClaw will restore the snapshot on next poll.`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rollback failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="border-b px-4 py-2.5 font-semibold text-sm">Rollback {change.change_id}</div>
        <div className="p-4 space-y-2 text-xs">
          <p>Restore the <b>before</b> snapshot? MyClaw will apply it on next poll.</p>
          <textarea value={reason} onChange={e => setReason(e.target.value)}
                    rows={3} placeholder="Why are you rolling back? (audit trail, min 5 chars)"
                    className="w-full border rounded p-2 text-xs" />
        </div>
        <div className="border-t px-4 py-2.5 flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant="destructive" onClick={submit} disabled={busy}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Confirm rollback
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * TAB 5: AI Drafts Review
 * ═══════════════════════════════════════════════════════════════════ */
export function AIDraftsTab() {
  const { data: list, loading, reload } = usePAv2("/ai-drafts?limit=100", []);
  const { data: metrics } = usePAv2("/ai-drafts/metrics?days=7", []);

  return (
    <div className="space-y-3" data-testid="pa-v2-aidrafts-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KPI label="Drafts (7d)" value={metrics?.total} testId="kpi-drafts-total" />
        <KPI label="Approved" value={metrics?.approved} accent="emerald" />
        <KPI label="Rejected" value={metrics?.rejected} accent="red" />
        <KPI label="Approval %" value={`${metrics?.approval_rate_pct ?? 0}%`} accent="indigo" />
        <KPI label="Edited %" value={`${metrics?.edit_rate_pct ?? 0}%`} accent="amber" />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Bot className="h-4 w-4 text-indigo-600" />
            AI Drafts ({list?.total || 0})
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={reload}><RotateCcw className="h-3.5 w-3.5" /></Button>
        </CardHeader>
        <CardContent>
          {loading ? <Loading /> : (list?.items || []).length === 0 ? (
            <EmptyState icon={Bot} title="No AI drafts yet"
              sub="When MyClaw generates a draft reply, it appears here for review." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-500 border-b">
                  <tr><th className="pb-2 pr-2">Time</th><th className="pb-2 pr-2">From</th>
                  <th className="pb-2 pr-2">Subject</th><th className="pb-2 pr-2">Status</th>
                  <th className="pb-2 pr-2">Template</th><th className="pb-2 pr-2">Tokens</th>
                  <th className="pb-2">Edits</th></tr>
                </thead>
                <tbody>
                  {list.items.slice(0, 100).map(d => (
                    <tr key={d.id} className="border-b border-slate-50 hover:bg-slate-50"
                        data-testid={`aidraft-row-${d.id}`}>
                      <td className="py-1.5 pr-2 text-xs text-slate-500">{fmtTs(d.created_at)}</td>
                      <td className="py-1.5 pr-2 text-xs truncate max-w-[160px]">{d.from}</td>
                      <td className="py-1.5 pr-2 text-xs truncate max-w-[280px]">{d.subject}</td>
                      <td className="py-1.5 pr-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        d.approval_status === "approved" || d.approval_status === "sent" ? "bg-green-100 text-green-700" :
                        d.approval_status === "rejected" ? "bg-red-100 text-red-700" :
                        "bg-slate-100 text-slate-600"}`}>{d.approval_status || "pending"}</span></td>
                      <td className="py-1.5 pr-2 text-xs">{d.template_used || "—"}</td>
                      <td className="py-1.5 pr-2 text-xs">{d.tokens_used || 0}</td>
                      <td className="py-1.5 text-xs">{d.operator_edits ? "✏️" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * TAB 6: RFQ Routing
 * ═══════════════════════════════════════════════════════════════════ */
export function RFQRoutingTab() {
  const [mode, setMode] = useState("");
  const { data, loading, reload } = usePAv2(
    `/rfq-routing${mode ? `?service_mode=${mode}` : ""}`, [mode]);
  const { data: stats } = usePAv2("/rfq-routing/stats?days=30", []);

  return (
    <div className="space-y-3" data-testid="pa-v2-rfq-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {["FCL", "LCL", "AIR", "ROAD", "PROJECT"].map(m => {
          const s = stats?.by_service_mode?.[m];
          return (
            <KPI key={m} label={m} value={s?.count ?? 0}
                 accent={m === "FCL" ? "blue" : m === "LCL" ? "cyan" : m === "AIR" ? "violet" : m === "ROAD" ? "amber" : "slate"}
                 testId={`rfq-kpi-${m.toLowerCase()}`} />
          );
        })}
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            RFQ Routing Log
          </CardTitle>
          <div className="flex items-center gap-2">
            <select value={mode} onChange={e => setMode(e.target.value)}
                    className="h-8 text-xs border rounded px-2"
                    data-testid="rfq-mode-filter">
              <option value="">All modes</option>
              {["FCL", "LCL", "AIR", "ROAD", "PROJECT"].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <Button size="sm" variant="ghost" onClick={reload}><RotateCcw className="h-3.5 w-3.5" /></Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <Loading /> : (data?.items || []).length === 0 ? (
            <EmptyState icon={TrendingUp} title="No RFQs routed yet"
              sub="When MyClaw classifies an inbound as an RFQ, the routing decision lands here." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-500 border-b">
                  <tr><th className="pb-2 pr-2">Time</th><th className="pb-2 pr-2">Subject</th>
                  <th className="pb-2 pr-2">Mode</th><th className="pb-2 pr-2">Pricing team</th>
                  <th className="pb-2 pr-2">Portal sent</th><th className="pb-2 pr-2">ERP quote</th>
                  <th className="pb-2 pr-2">Dup</th><th className="pb-2">Lat</th></tr>
                </thead>
                <tbody>
                  {data.items.map(r => (
                    <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="py-1.5 pr-2 text-xs text-slate-500">{fmtTs(r.created_at)}</td>
                      <td className="py-1.5 pr-2 text-xs truncate max-w-[260px]">{r.subject}</td>
                      <td className="py-1.5 pr-2"><span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-mono">{r.service_mode}</span></td>
                      <td className="py-1.5 pr-2 text-xs">{r.assigned_pricing_team || "—"}</td>
                      <td className="py-1.5 pr-2 text-xs">{r.quote_portal_sent ? "✓" : "—"}</td>
                      <td className="py-1.5 pr-2 text-xs font-mono">{r.erp_quote_number || "—"}</td>
                      <td className="py-1.5 pr-2 text-xs">{r.duplicate_prevented ? "🛡" : "—"}</td>
                      <td className="py-1.5 text-xs text-slate-500">{r.processing_latency_ms}ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * TAB 7: Token Usage
 * ═══════════════════════════════════════════════════════════════════ */
export function TokenUsageTab() {
  const { data, loading, reload } = usePAv2("/llm-usage?days=30", []);
  const state = data?.state;
  const stateColor = {
    ok: "emerald", notice: "blue", warn: "amber", alert: "orange", hard_stop: "red",
  }[state] || "slate";

  return (
    <div className="space-y-3" data-testid="pa-v2-tokens-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KPI label="Used (mo)" value={(data?.monthly_used_tokens || 0).toLocaleString()} />
        <KPI label="Budget" value={(data?.monthly_budget_tokens || 0).toLocaleString()} accent="slate" />
        <KPI label="% used" value={`${data?.monthly_pct ?? 0}%`} accent={stateColor} testId="kpi-pct" />
        <KPI label="Blocked" value={data?.blocked_calls ?? 0} accent="red" />
        <KPI label="State" value={state || "ok"} accent={stateColor} testId="kpi-state" />
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-600" /> LLM Usage (last 30d)
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={reload}><RotateCcw className="h-3.5 w-3.5" /></Button>
        </CardHeader>
        <CardContent>
          {loading ? <Loading /> : (
            <>
              {data?.llm_disabled && (
                <div className="bg-red-50 border border-red-200 text-red-800 text-xs rounded p-2 mb-3 flex items-center gap-2">
                  <Ban className="h-4 w-4" /> <b>LLM is currently DISABLED</b> (kill-switch or budget hard-stop).
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-2 font-medium uppercase">Daily usage</p>
                  <div className="space-y-1">
                    {(data?.daily || []).slice(-14).map(d => (
                      <div key={d.day} className="flex items-center gap-2 text-xs">
                        <span className="text-slate-500 w-20 font-mono">{d.day}</span>
                        <div className="flex-1 bg-slate-100 rounded h-4 overflow-hidden">
                          <div className="bg-indigo-400 h-full"
                               style={{width: `${Math.min(100, (d.tokens / 50000) * 100)}%`}} />
                        </div>
                        <span className="text-slate-700 w-20 text-right">{d.tokens.toLocaleString()}</span>
                      </div>
                    ))}
                    {(data?.daily || []).length === 0 && <p className="text-xs text-slate-400">No usage data yet.</p>}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-2 font-medium uppercase">By category</p>
                  <div className="space-y-1">
                    {Object.entries(data?.by_category || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between text-xs">
                        <span className="text-slate-700">{k}</span>
                        <span className="font-mono text-slate-500">{v.toLocaleString()}</span>
                      </div>
                    ))}
                    {Object.keys(data?.by_category || {}).length === 0 && <p className="text-xs text-slate-400">No category breakdown.</p>}
                  </div>
                  <p className="text-xs text-slate-500 mt-4 mb-2 font-medium uppercase">Thresholds</p>
                  <div className="space-y-1 text-xs">
                    {Object.entries(data?.thresholds || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between"><span className="text-slate-600">{k}</span><span className="font-mono">{v}%</span></div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * TAB 8: Audit Explorer
 * ═══════════════════════════════════════════════════════════════════ */
export function AuditExplorerTab() {
  const [form, setForm] = useState({
    pf: "", sender: "", thread_id: "", container: "", category: "", escalation_id: "",
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    const qs = new URLSearchParams(
      Object.entries(form).filter(([_, v]) => v && v.trim()).map(([k, v]) => [k, v.trim()])
    ).toString();
    if (!qs) { toast.error("Enter at least one filter"); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/pa/v2/audit/search?${qs}`, api());
      setResults(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Search failed"); }
    finally { setLoading(false); }
  };

  const chain = ["FETCH", "CLASSIFY", "ERP_LOOKUP", "POLICY", "TEMPLATE", "VALIDATOR", "SEND"];

  return (
    <div className="space-y-3" data-testid="pa-v2-audit-tab">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileSearch className="h-4 w-4 text-blue-600" /> Audit Explorer
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
            <Input placeholder="PF number (e.g. PF-000542-26)" value={form.pf}
                   onChange={e => setForm({...form, pf: e.target.value})}
                   className="h-8 text-xs" data-testid="audit-pf" />
            <Input placeholder="Sender email contains…" value={form.sender}
                   onChange={e => setForm({...form, sender: e.target.value})}
                   className="h-8 text-xs" data-testid="audit-sender" />
            <Input placeholder="Thread ID" value={form.thread_id}
                   onChange={e => setForm({...form, thread_id: e.target.value})}
                   className="h-8 text-xs" />
            <Input placeholder="Container (e.g. MSKU5159705)" value={form.container}
                   onChange={e => setForm({...form, container: e.target.value})}
                   className="h-8 text-xs" data-testid="audit-container" />
            <Input placeholder="Category (e.g. rfq, eta)" value={form.category}
                   onChange={e => setForm({...form, category: e.target.value})}
                   className="h-8 text-xs" />
            <Input placeholder="Escalation ID" value={form.escalation_id}
                   onChange={e => setForm({...form, escalation_id: e.target.value})}
                   className="h-8 text-xs" />
          </div>
          <Button onClick={search} disabled={loading} className="gap-1" data-testid="audit-search-btn">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
            Search
          </Button>
        </CardContent>
      </Card>

      {results && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {results.count} audit rows · grouped into {Object.keys(results.by_email || {}).length} email(s)
              <span className="text-xs font-normal text-slate-400 ml-2">source: {results.source}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.entries(results.by_email || {}).length === 0 ? (
              <EmptyState icon={Search} title="No matching audit rows" />
            ) : (
              <div className="space-y-3">
                {Object.entries(results.by_email).map(([eid, rows]) => (
                  <div key={eid} className="border rounded p-2">
                    <p className="font-mono text-xs text-violet-600 mb-1.5">{eid}</p>
                    <div className="flex items-center gap-1 flex-wrap">
                      {chain.map(stage => {
                        const hit = rows.find(r => (r.stage || "").toUpperCase() === stage);
                        return (
                          <span key={stage}
                            className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${hit ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}
                            title={hit ? `${stage}: ${hit.detail || hit.result || "ok"} (${hit.latency_ms || 0}ms)` : `${stage} not recorded`}>
                            {stage}{hit ? " ✓" : ""}
                          </span>
                        );
                      })}
                    </div>
                    <details className="mt-1.5">
                      <summary className="text-[10px] text-slate-500 cursor-pointer">raw rows ({rows.length})</summary>
                      <pre className="text-[10px] bg-slate-50 rounded p-1.5 mt-1 overflow-x-auto max-h-60">{JSON.stringify(rows, null, 2)}</pre>
                    </details>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * TAB 9: Daemon Health (expanded)
 * ═══════════════════════════════════════════════════════════════════ */
export function PAAutomationToggles() {
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/pa/operator/mailboxes`, api());
      setMailboxes(r.data.mailboxes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load PA mailboxes");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (mb, enabled) => {
    setBusy(mb);
    setMailboxes(prev => prev.map(m => m.mailbox === mb ? { ...m, enabled } : m));
    try {
      await axios.post(`${API_URL}/pa/operator/mailboxes/toggle`, { mailbox: mb, enabled }, api());
      toast.success(`PA turned ${enabled ? "ON" : "OFF"} for ${mb}`);
    } catch (e) {
      toast.error("Toggle failed");
      load();
    } finally { setBusy(null); }
  };

  const toggleAutoSend = async (mb, auto_send) => {
    setBusy(mb);
    setMailboxes(prev => prev.map(m => m.mailbox === mb ? { ...m, auto_send } : m));
    try {
      await axios.post(`${API_URL}/pa/operator/mailboxes/toggle`, { mailbox: mb, auto_send }, api());
      toast.success(auto_send ? `Auto-send LIVE for ${mb} — the PA will now reply automatically` : `Auto-send OFF for ${mb} (draft-only)`);
    } catch (e) {
      toast.error("Auto-send toggle failed");
      load();
    } finally { setBusy(null); }
  };

  const [newMailbox, setNewMailbox] = useState("");
  const addMailbox = async () => {
    const mb = newMailbox.trim().toLowerCase();
    if (!mb || !mb.includes("@")) { toast.error("Enter a valid email address"); return; }
    try {
      await axios.post(`${API_URL}/pa/operator/mailboxes/toggle`, { mailbox: mb, enabled: true }, api());
      toast.success(`Mailbox added & PA turned ON: ${mb}`);
      setNewMailbox("");
      load();
    } catch (e) { toast.error("Could not add mailbox"); }
  };

  return (
    <Card className="mb-4" data-testid="pa-automation-toggles">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Power className="h-4 w-4 text-violet-600" /> PA Automation — per mailbox
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={load} data-testid="pa-mailboxes-reload"><RotateCcw className="h-3.5 w-3.5" /></Button>
      </CardHeader>
      <CardContent>
        {loading ? <Loading /> : mailboxes.length === 0 ? (
          <EmptyState icon={Power} title="No mailboxes configured" sub="Set PA_OPERATOR_MAILBOXES to add mailboxes." />
        ) : (
          <div className="space-y-2">
            {mailboxes.map(m => (
              <div key={m.mailbox} data-testid={`pa-mailbox-row-${m.mailbox}`}
                   className="border border-slate-100 rounded-lg px-3 py-2.5 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`h-2 w-2 rounded-full shrink-0 ${m.enabled ? "bg-emerald-500" : "bg-slate-300"}`} />
                    <span className="text-sm font-medium truncate">{m.mailbox}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${m.enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {m.enabled ? "PA ON" : "PA OFF"}
                    </span>
                  </div>
                  <Switch checked={!!m.enabled} disabled={busy === m.mailbox}
                          onCheckedChange={(v) => toggle(m.mailbox, v)}
                          data-testid={`pa-toggle-${m.mailbox}`} />
                </div>
                <div className={`flex items-center justify-between rounded-md px-2 py-1.5 ${m.auto_send ? "bg-rose-50" : "bg-slate-50"} ${!m.enabled ? "opacity-50" : ""}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Send className="h-3.5 w-3.5 text-rose-500 shrink-0" />
                    <span className="text-xs font-medium text-slate-600">Auto-send replies</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${m.auto_send ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-500"}`}>
                      {m.auto_send ? "LIVE" : "draft-only"}
                    </span>
                  </div>
                  <Switch checked={!!m.auto_send} disabled={busy === m.mailbox || !m.enabled}
                          onCheckedChange={(v) => toggleAutoSend(m.mailbox, v)}
                          data-testid={`pa-autosend-toggle-${m.mailbox}`} />
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 mt-3" data-testid="pa-add-mailbox-row">
          <input
            type="email"
            value={newMailbox}
            onChange={(e) => setNewMailbox(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addMailbox()}
            placeholder="Add a mailbox to monitor (e.g. anja.rakar@europacific.com)"
            className="flex-1 text-sm border border-slate-300 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400"
            data-testid="pa-add-mailbox-input"
          />
          <Button size="sm" onClick={addMailbox} data-testid="pa-add-mailbox-btn">Add mailbox</Button>
        </div>
        <p className="text-xs text-slate-400 mt-3">This is the master reply switch. When <b>PA OFF</b>, the daemon skips that mailbox entirely — no drafts, no escalations, no auto-replies. <span className="text-rose-500 font-medium">Auto-send LIVE</span> lets the PA reply automatically, but only for the trusted <b>Automation Levels</b> above (Out-of-office, Shipment/PF status, Learned templates, and High-trust ≥85%). RFQ/pricing, complaints, multi-recipient threads and safety-flagged drafts are always <b>held in the Approval Queue</b> for you. Replies send from your own mailbox with the display name <b>"Personal assistant"</b> so they land in your Sent folder. <span className="italic">(The "PA Access" column on the Users tab is different — it only controls who may use the PA plugin.)</span></p>
      </CardContent>
    </Card>
  );
}

export function PATeachPanel({ prefill }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [instruction, setInstruction] = useState("");
  const [category, setCategory] = useState("");
  const [action, setAction] = useState("draft");
  const [submitting, setSubmitting] = useState(false);
  const [drag, setDrag] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [parsing, setParsing] = useState(false);
  const panelRef = useRef(null);
  const [fromAction, setFromAction] = useState(null);
  const lastPrefill = useRef(null);

  // When a Recent Action is clicked, prefill this panel to teach/correct it.
  useEffect(() => {
    if (!prefill || !prefill.nonce || prefill.nonce === lastPrefill.current) return;
    lastPrefill.current = prefill.nonce;
    setFile(null);
    setSubject(prefill.subject || "");
    setBody(prefill.body || "");
    setCategory(prefill.category || "");
    setAction(prefill.action || "draft");
    setInstruction(prefill.instruction || "");
    setPreview(null);
    setFromAction({
      sender: prefill.sender,
      reference: prefill.reference,
      prev_action: prefill.prev_action,
      confidence: prefill.confidence,
    });
    setTimeout(() => {
      panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    toast.info("Loaded into Teach the PA — adjust the instruction and Teach PA");
  }, [prefill]);

  const parseFile = useCallback(async (f) => {
    if (!f) return;
    setFile(f);
    setParsing(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API_URL}/pa/operator/teach/parse`, fd, api());
      const d = r.data || {};
      if (d.subject) setSubject(d.subject);
      if (d.body) setBody(d.body);
      if (d.parsed) toast.success("Email parsed — subject & body filled in");
      else toast.warning("Could not read the email content — you can paste it below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not parse the email file");
    } finally { setParsing(false); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/pa/operator/teachings`, api());
      setItems(r.data.teachings || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runPreview = async () => {
    if (!instruction.trim()) { toast.error("Add an instruction — tell the PA what to do"); return; }
    if (!file && !subject.trim() && !body.trim()) { toast.error("Drop an email file or paste subject/body"); return; }
    setPreviewing(true);
    setPreview(null);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      fd.append("subject", subject);
      fd.append("body", body);
      fd.append("instruction", instruction);
      fd.append("category", category);
      fd.append("action", action);
      const r = await axios.post(`${API_URL}/pa/operator/teach/preview`, fd, api());
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setPreviewing(false); }
  };

  const submit = async () => {
    if (!instruction.trim()) { toast.error("Add an instruction — tell the PA what to do"); return; }
    if (!file && !subject.trim() && !body.trim()) { toast.error("Drop an email file or paste subject/body"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      fd.append("subject", subject);
      fd.append("body", body);
      fd.append("instruction", instruction);
      fd.append("category", category);
      fd.append("action", action);
      await axios.post(`${API_URL}/pa/operator/teach`, fd, api());
      toast.success("PA taught — it will handle similar emails this way");
      setFile(null); setSubject(""); setBody(""); setInstruction(""); setCategory(""); setAction("draft"); setPreview(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Teach failed"); }
    finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API_URL}/pa/operator/teach/${id}`, api());
      toast.success("Teaching removed");
      setItems(p => p.filter(x => x.id !== id));
    } catch (e) { toast.error("Delete failed"); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  return (
    <Card className="mb-4" data-testid="teach-panel" ref={panelRef}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-500" /> Teach the PA
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">Drop an email and tell the PA what to do. It applies your instruction to similar emails — getting smarter every day.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {fromAction && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs flex items-start gap-2" data-testid="teach-from-action">
            <Sparkles className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
            <div className="flex-1">
              <span className="font-semibold text-amber-900">Correcting a recent action</span>
              <div className="text-amber-800 mt-0.5">
                {fromAction.sender ? <span>From <b>{fromAction.sender}</b>. </span> : null}
                {fromAction.prev_action ? <span>PA previously chose <b>{String(fromAction.prev_action).replace(/_/g, " ")}</b>{fromAction.confidence ? ` (${Math.round(fromAction.confidence * 100)}%)` : ""}. </span> : null}
                Adjust the instruction below, then <b>Teach PA</b>.
              </div>
            </div>
            <button onClick={() => setFromAction(null)} className="text-amber-500 hover:text-amber-700" data-testid="teach-from-action-dismiss">✕</button>
          </div>
        )}
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          data-testid="teach-dropzone"
          className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${drag ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}
        >
          <Inbox className="h-6 w-6 mx-auto text-slate-400 mb-1" />
          {parsing ? (
            <p className="text-sm font-medium text-blue-700 flex items-center justify-center gap-1.5" data-testid="teach-file-parsing"><Loader2 className="h-4 w-4 animate-spin" /> Reading email…</p>
          ) : file ? (
            <p className="text-sm font-medium text-emerald-700" data-testid="teach-file-name">{file.name}</p>
          ) : (
            <p className="text-xs text-slate-500">Drag & drop a .msg / .eml / .txt email here, or</p>
          )}
          <label className="inline-block mt-2 text-xs text-blue-600 cursor-pointer underline">
            browse file
            <input type="file" accept=".msg,.eml,.txt" className="hidden" data-testid="teach-file-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) parseFile(f); }} />
          </label>
        </div>

        <div className="text-[11px] text-slate-400 text-center">— or paste the email —</div>
        <Input placeholder="Email subject (optional if file dropped)" value={subject}
          onChange={(e) => setSubject(e.target.value)} data-testid="teach-subject" />
        <textarea placeholder="Email body (optional if file dropped)" value={body}
          onChange={(e) => setBody(e.target.value)} data-testid="teach-body" rows={3}
          className="w-full border border-slate-200 rounded-lg p-2 text-sm" />

        <textarea placeholder="What should the PA do with emails like this? (e.g. 'Forward demurrage questions to the ocean team and draft a holding reply')"
          value={instruction} onChange={(e) => setInstruction(e.target.value)} data-testid="teach-instruction" rows={2}
          className="w-full border border-slate-300 rounded-lg p-2 text-sm" />

        <div className="flex flex-wrap gap-2">
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="teach-category"
            className="border border-slate-200 rounded-lg px-2 py-2 text-sm">
            <option value="">Category: auto</option>
            {["shipment_status","eta_request","document_request","pod_request","company_document","rfq","accounting","bank_details_request","complaint","booking","pricing","relationship_engagement","internal_request"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="teach-action"
            className="border border-slate-200 rounded-lg px-2 py-2 text-sm">
            <option value="draft">Draft a reply</option>
            <option value="escalate">Escalate to human</option>
            <option value="skip">Ignore / track only</option>
          </select>
          <Button variant="outline" onClick={runPreview} disabled={previewing || submitting} data-testid="teach-preview-btn" className="ml-auto">
            {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Eye className="h-4 w-4 mr-1.5" /> Preview</>}
          </Button>
          <Button onClick={submit} disabled={submitting || previewing} data-testid="teach-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1.5" /> Teach PA</>}
          </Button>
        </div>

        {preview && (
          <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 space-y-3" data-testid="teach-preview-panel">
            <div className="flex items-center gap-2">
              <Bot className="h-4 w-4 text-blue-600" />
              <p className="text-sm font-semibold text-blue-900">Here's what the PA will do</p>
              <span className="ml-auto text-[11px] uppercase tracking-wide font-mono px-2 py-0.5 rounded bg-blue-600 text-white" data-testid="teach-preview-action">
                {preview.action === "draft" ? "Draft reply" : preview.action === "escalate" ? "Escalate" : "Skip"}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs">
              {preview.reference ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-slate-200" data-testid="teach-preview-reference">
                  <FileText className="h-3 w-3 text-slate-500" /> Ref: <b>{preview.reference}</b>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-slate-200 text-slate-500">No PF reference detected</span>
              )}
              {preview.reference && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${preview.pf_found ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`} data-testid="teach-preview-pf-status">
                  {preview.pf_found ? <><CheckCircle2 className="h-3 w-3" /> PF found</> : <><HelpCircle className="h-3 w-3" /> PF not found</>}
                </span>
              )}
              {preview.language && (
                <span className="inline-flex items-center px-2 py-0.5 rounded bg-white border border-slate-200 text-slate-500 uppercase">{preview.language}</span>
              )}
            </div>

            {preview.understood && (
              <div className="text-xs text-slate-700">
                <p className="font-medium text-slate-500 mb-0.5">Understood action</p>
                <p data-testid="teach-preview-understood">{preview.understood}</p>
              </div>
            )}

            {Array.isArray(preview.bl_documents) && preview.bl_documents.length > 0 && (
              <div className="text-xs" data-testid="teach-preview-bl-docs">
                <p className="font-medium text-slate-500 mb-1 flex items-center gap-1"><Paperclip className="h-3 w-3" /> Bill of Lading documents on file ({preview.bl_documents.length})</p>
                <ul className="list-disc pl-5 space-y-0.5 text-slate-700">
                  {preview.bl_documents.map((d, i) => (
                    <li key={i} className={preview.attachments?.some(a => a.filename === d.filename) ? "text-emerald-700 font-medium" : ""}>
                      {d.filename}{preview.attachments?.some(a => a.filename === d.filename) ? " (will be attached)" : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {preview.reply && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Drafted reply</p>
                <div className="bg-white border-l-2 border-blue-400 rounded p-2.5 text-sm text-slate-800 whitespace-pre-wrap max-h-72 overflow-y-auto" data-testid="teach-preview-reply">
                  {preview.reply}
                </div>
              </div>
            )}

            <p className="text-[11px] text-slate-400">This is a preview only — nothing is sent. Click <b>Teach PA</b> to save this rule.</p>
          </div>
        )}

        <div className="pt-2 border-t border-slate-100">
          <p className="text-xs font-medium text-slate-600 mb-2">Learned rules ({items.length})</p>
          {loading ? <Loading /> : items.length === 0 ? (
            <p className="text-xs text-slate-400">No teachings yet.</p>
          ) : (
            <div className="space-y-2">
              {items.map(t => (
                <div key={t.id} data-testid={`teach-item-${t.id}`} className="flex items-start justify-between gap-2 border border-slate-100 rounded-lg px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{t.instruction}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {t.match_sender ? `from ${t.match_sender} · ` : ""}{t.category} → {t.action}
                      {t.times_applied ? ` · applied ${t.times_applied}×` : ""}
                    </p>
                  </div>
                  <button onClick={() => remove(t.id)} data-testid={`teach-delete-${t.id}`} className="text-slate-400 hover:text-rose-600 shrink-0">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function AutoSentTodayPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/pa/operator/autosent-today?hours=24`, api());
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load auto-sent audit");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  return (
    <Card className="mb-4" data-testid="pa-autosent-today">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Send className="h-4 w-4 text-sky-600" /> Auto-sent today
          {data && (
            <span className="text-xs font-normal text-slate-500">
              · <span className="text-emerald-600 font-medium">{data.sent_count}</span> sent
              {data.superseded_count > 0 && <> · <span className="text-amber-600 font-medium">{data.superseded_count}</span> superseded</>}
              <span className="text-slate-400"> (last {data.window_hours}h)</span>
            </span>
          )}
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={load} data-testid="pa-autosent-reload"><RotateCcw className="h-3.5 w-3.5" /></Button>
      </CardHeader>
      <CardContent>
        {loading ? <Loading /> : items.length === 0 ? (
          <EmptyState icon={Send} title="Nothing auto-sent in the last 24h"
                      sub="When a mailbox has Auto-send ON and the PA replies, each reply shows here with its exact text so you can spot-check it." />
        ) : (
          <div className="space-y-2">
            {items.map((it) => {
              const isOpen = !!open[it.email_id];
              const superseded = it.status === "superseded_by_human";
              return (
                <div key={it.email_id} data-testid={`pa-autosent-row-${it.email_id}`}
                     className="border border-slate-100 rounded-lg overflow-hidden">
                  <button
                    className="w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors flex items-start justify-between gap-3"
                    onClick={() => setOpen(p => ({ ...p, [it.email_id]: !p[it.email_id] }))}
                    data-testid={`pa-autosent-toggle-${it.email_id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${superseded ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
                          {superseded ? "superseded by human" : "auto-sent"}
                        </span>
                        {it.category && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{it.category}</span>}
                        {it.is_ooh && <span className="text-xs px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">out-of-hours</span>}
                      </div>
                      <p className="text-sm font-medium truncate mt-1">{it.subject || "(no subject)"}</p>
                      <p className="text-xs text-slate-500 truncate">
                        to <span className="font-medium">{it.from_email || it.from || "—"}</span>
                        {" · from "}<span className="font-medium">{it.to_email || "—"}</span>
                      </p>
                    </div>
                    <span className="text-xs text-slate-400 font-mono shrink-0">{fmtTs(it.sent_at || it.updated_at)}</span>
                  </button>
                  {isOpen && (
                    <div className="px-3 pb-3 pt-1 bg-slate-50 border-t border-slate-100" data-testid={`pa-autosent-body-${it.email_id}`}>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Exact reply sent</p>
                      <pre className="text-xs whitespace-pre-wrap font-sans text-slate-700 bg-white rounded p-2 border border-slate-100 max-h-64 overflow-y-auto">{it.proposed_reply || "(no reply text stored)"}</pre>
                      {(it.routing_cc || []).length > 0 && (
                        <p className="text-xs text-slate-500 mt-2">CC: {(it.routing_cc || []).join(", ")}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function DaemonHealthTab({ teachPrefill }) {
  const { data, loading, reload } = usePAv2("/daemon-health", []);

  return (
    <>
    <PAAutomationToggles />
    <AutoSentTodayPanel />
    <PATeachPanel prefill={teachPrefill} />
    <Card data-testid="pa-v2-health-tab">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Gauge className="h-4 w-4 text-emerald-600" /> Daemon Health
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={reload}><RotateCcw className="h-3.5 w-3.5" /></Button>
      </CardHeader>
      <CardContent>
        {loading ? <Loading /> : !data ? <EmptyState icon={Gauge} title="No health data" /> : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <KPI label="Graph API ms" value={data.graph_api_latency_ms ?? "—"}
                 accent={(data.graph_api_latency_ms || 0) > 2000 ? "red" : "emerald"} />
            <KPI label="ERP ms" value={data.erp_latency_ms ?? "—"}
                 accent={(data.erp_latency_ms || 0) > 2000 ? "red" : "emerald"} />
            <KPI label="LLM ms" value={data.llm_latency_ms ?? "—"} />
            <KPI label="Queue backlog" value={data.queue_backlog} accent="amber" />
            <KPI label="Failed jobs" value={data.failed_jobs} accent="red" />
            <KPI label="Dead-letter" value={data.dead_letter} accent="red" />
            <KPI label="Replays pending" value={data.pending_replays} accent="violet" />
            <KPI label="Open escalations" value={data.open_escalations} accent="orange" />
          </div>
        )}
        {data && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <div className="bg-slate-50 rounded p-2">
              <p className="text-slate-500 uppercase tracking-wider mb-1">Last successful</p>
              <p className="font-mono">{fmtTs(data.last_successful_at || data.last_run_at)}</p>
            </div>
            <div className="bg-slate-50 rounded p-2">
              <p className="text-slate-500 uppercase tracking-wider mb-1">Teams webhook</p>
              <p className="font-mono">{data.teams_webhook_healthy ? "✓ healthy" : "⚠ degraded"}</p>
            </div>
            <div className="bg-slate-50 rounded p-2">
              <p className="text-slate-500 uppercase tracking-wider mb-1">LLM provider</p>
              <p className="font-mono">{data.llm_provider_healthy ? "✓ healthy" : "⚠ degraded"}</p>
            </div>
            <div className="bg-slate-50 rounded p-2">
              <p className="text-slate-500 uppercase tracking-wider mb-1">Snapshot</p>
              <p className="font-mono">{fmtTs(data.timestamp)}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * Inline escalation v2-context block — drops into the existing detail
 * pane in PAOperationsPage. Adds: routing decision, blocked rules,
 * processing chain.
 * ═══════════════════════════════════════════════════════════════════ */
export function EscalationV2Context({ emailId }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!emailId) return;
    let alive = true;
    setLoading(true);
    axios.get(`${API_URL}/pa/v2/escalations/${emailId}`, api())
      .then(r => { if (alive) setD(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [emailId]);

  if (!emailId) return null;
  if (loading) return <div className="p-3 text-xs text-slate-400">Loading control-tower context…</div>;
  if (!d) return null;

  const chain = ["FETCH", "CLASSIFY", "ERP_LOOKUP", "POLICY", "TEMPLATE", "VALIDATOR", "SEND"];

  return (
    <div className="px-4 pb-1 pt-3 border-b space-y-3" data-testid="esc-v2-context">
      {/* Processing chain */}
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Processing chain</p>
        <div className="flex items-center gap-1 flex-wrap">
          {chain.map(stage => {
            const hit = (d.audit_chain || []).find(
              r => (r.stage || "").toUpperCase() === stage);
            return (
              <span key={stage}
                className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${hit ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}
                title={hit ? `${stage}: ${hit.detail || hit.result || "ok"}` : `${stage} not recorded`}>
                {stage}{hit ? " ✓" : ""}
              </span>
            );
          })}
        </div>
      </div>

      {/* Routing decision */}
      {d.routing_decision && Object.values(d.routing_decision).some(v => v) && (
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Routing decision</p>
          <div className="bg-violet-50 border border-violet-200 rounded p-2 text-xs space-y-0.5">
            {d.routing_decision.module && <div><b>Module:</b> {d.routing_decision.module}</div>}
            {d.routing_decision.rule && <div><b>Rule:</b> <span className="font-mono">{d.routing_decision.rule}</span></div>}
            {d.routing_decision.policy && <div><b>Policy:</b> {d.routing_decision.policy}</div>}
            {d.routing_decision.confidence != null && <div><b>Confidence:</b> {Math.round(d.routing_decision.confidence * 100)}%</div>}
          </div>
        </div>
      )}

      {/* Blocked rules */}
      {(d.blocked_rules || []).length > 0 && (
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Blocked rules ({d.blocked_rules.length})</p>
          <div className="space-y-1">
            {d.blocked_rules.map((b, i) => (
              <div key={b.id || i} className="bg-red-50 border border-red-200 rounded p-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] px-1.5 py-0.5 bg-red-200 text-red-800 rounded font-mono">{b.category || "blocked"}</span>
                  <span className="font-mono text-red-700">{b.blocked_phrase || b.rule}</span>
                </div>
                {b.validator_reason && <p className="text-slate-600 mt-1">{b.validator_reason}</p>}
                {b.fallback_action && <p className="text-slate-500 mt-0.5">Fallback: {b.fallback_action}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * Expanded Metrics — drop-in panel for the existing Metrics tab.
 * ═══════════════════════════════════════════════════════════════════ */
export function ExpandedMetricsPanel() {
  const { data, loading, reload } = usePAv2("/metrics/expanded?days=7", []);
  if (loading) return <Loading />;
  if (!data) return null;
  return (
    <Card data-testid="pa-v2-metrics-expanded">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-blue-600" /> Operational Metrics (7d)
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={reload}><RotateCcw className="h-3.5 w-3.5" /></Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <KPI label="Total emails" value={data.total_emails} />
          <KPI label="Escalated" value={data.escalated} accent="red" />
          <KPI label="Escalation %" value={`${data.escalation_rate_pct}%`} accent="orange" />
          <KPI label="Drafts" value={data.drafts} accent="indigo" />
          <KPI label="Approval %" value={`${data.approval_rate_pct}%`} accent="emerald" />
          <KPI label="Template-only" value={data.template_only_drafts} accent="slate" />
          <KPI label="AI drafts" value={data.ai_drafts} accent="violet" />
          <KPI label="Tpl/AI ratio" value={data.template_to_ai_ratio ?? "—"} />
          <KPI label="Unsafe blocked" value={data.unsafe_blocked} accent="red" />
          <KPI label="REPORT correc." value={data.report_corrections} accent="amber" />
          <KPI label="RFQ total" value={data.rfq_total} accent="emerald" />
          <KPI label="RFQ auto %" value={`${data.rfq_auto_route_pct}%`} accent="emerald" />
          <KPI label="Avg latency" value={data.avg_processing_latency_ms ? `${data.avg_processing_latency_ms}ms` : "—"} />
        </div>
      </CardContent>
    </Card>
  );
}


/* ═════════════════════════════════════════════════════════════════════
 * TELEMETRY BANNER — top-of-page health strip
 *   • Queue counts (Incoming / Escalations / Replay / Dead-letter)
 *   • SLA timers   (oldest open item per queue, green/amber/red)
 *   • Routing accuracy (auto-route %, scope guards, topic drift, …)
 *
 * Read-only. Polls /api/pa/v2/telemetry every 45s.
 * ═══════════════════════════════════════════════════════════════════ */

const SLA_COLORS = {
  green: { dot: "bg-emerald-500", text: "text-emerald-700", border: "border-emerald-200", bg: "bg-emerald-50" },
  amber: { dot: "bg-amber-500",   text: "text-amber-700",   border: "border-amber-200",   bg: "bg-amber-50" },
  red:   { dot: "bg-red-500",     text: "text-red-700",     border: "border-red-200",     bg: "bg-red-50" },
};

const _fmtAge = (mins) => {
  if (mins == null) return "—";
  const abs = Math.abs(mins);
  let v;
  if (abs < 60)   v = `${Math.round(abs)}m`;
  else if (abs < 1440) v = `${(abs / 60).toFixed(1)}h`;
  else v = `${(abs / 1440).toFixed(1)}d`;
  return mins < 0 ? `-${v}` : v;
};

const _fmtRemaining = (rem) => {
  if (rem == null) return "—";
  if (rem >= 0) return `${_fmtAge(rem)} left`;
  return `${_fmtAge(rem)} over`;
};

const QueueChip = ({ label, value, accent = "slate", testId, breach }) => (
  <div
    className={`relative bg-white rounded-lg border px-3 py-2 ${breach ? "border-red-300 bg-red-50" : `border-${accent}-100`}`}
    data-testid={testId}
  >
    <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`text-2xl font-bold ${breach ? "text-red-700" : `text-${accent}-700`}`}>
      {value ?? 0}
    </p>
    {breach ? (
      <span className="absolute top-1 right-1 inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
    ) : null}
  </div>
);

/**
 * SLACard — renders a per-queue SLA block driven by MyClaw's category SLAs.
 * Visualises: counts by colour, worst open item (elapsed / target / remaining
 * / breached), and a per-category mini breakdown.
 */
const SLACard = ({ slot, testId }) => {
  if (!slot) return null;
  const s = SLA_COLORS[slot.state] || SLA_COLORS.green;
  const worst = slot.worst;
  const worstColor = worst ? (SLA_COLORS[worst.color] || SLA_COLORS.green) : s;

  // Up to 4 categories with non-zero open items.
  const cats = Object.entries(slot.by_category || {})
    .map(([cat, c]) => ({
      cat,
      total: (c.green || 0) + (c.amber || 0) + (c.red || 0),
      red: c.red || 0,
      amber: c.amber || 0,
      green: c.green || 0,
      target: c.target_min,
    }))
    .sort((a, b) => b.red - a.red || b.amber - a.amber || b.total - a.total)
    .slice(0, 4);

  return (
    <div
      className={`rounded-lg border ${s.border} ${s.bg} px-3 py-2.5 space-y-2`}
      data-testid={testId}
    >
      {/* Header: queue name + green/amber/red counts */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${s.dot}`} />
          <p className={`text-[11px] uppercase tracking-wider font-medium ${s.text} truncate`}>
            {slot.label}
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-mono">
          <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{slot.green ?? 0}</span>
          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{slot.amber ?? 0}</span>
          <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold">{slot.red ?? 0}</span>
        </div>
      </div>

      {/* Worst open item: elapsed / remaining / breached */}
      {worst ? (
        <div className="bg-white/60 rounded border border-slate-100 px-2 py-1.5">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider truncate">
              {worst.category} · target {_fmtAge(worst.target_min)}
            </p>
            <p className={`text-[10px] font-bold ${worstColor.text} uppercase`}>
              {worst.breached ? "BREACHED" : `${Math.round(worst.pct)}%`}
            </p>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className={`text-base font-bold ${worstColor.text}`}>
              {_fmtAge(worst.elapsed_min)} elapsed
            </span>
            <span className={`text-[11px] ${worstColor.text}`}>
              {_fmtRemaining(worst.remaining_min)}
            </span>
          </div>
          {worst.id ? (
            <p className="text-[9px] text-slate-400 mt-0.5 truncate" title={worst.id}>
              {worst.id}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-[11px] text-slate-400 italic">No open items</p>
      )}

      {/* Per-category mini breakdown */}
      {cats.length > 0 ? (
        <div className="space-y-0.5">
          {cats.map((c) => (
            <div key={c.cat} className="flex items-center justify-between gap-2 text-[10px]">
              <span className="text-slate-600 truncate flex-1" title={c.cat}>
                {c.cat} <span className="text-slate-400">· {_fmtAge(c.target)}</span>
              </span>
              <span className="font-mono text-slate-500">
                {c.red ? <span className="text-red-700 font-bold">{c.red}</span> : null}
                {c.red && (c.amber || c.green) ? " / " : null}
                {c.amber ? <span className="text-amber-700">{c.amber}</span> : null}
                {c.amber && c.green ? " / " : null}
                {c.green ? <span className="text-emerald-700">{c.green}</span> : null}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export function TelemetryBanner() {
  const { data, loading, reload } = usePAv2("/telemetry?window_days=7", []);
  // auto-refresh every 45 s
  useEffect(() => {
    const id = setInterval(reload, 45000);
    return () => clearInterval(id);
  }, [reload]);

  if (loading && !data) {
    return (
      <Card data-testid="pa-v2-telemetry-banner">
        <CardContent className="py-4"><Loading /></CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const q = data.queues || {};
  const sla = data.sla || {};
  const r = data.routing || {};

  return (
    <Card data-testid="pa-v2-telemetry-banner" className="border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Gauge className="h-4 w-4 text-blue-600" />
          Control Tower Telemetry
          <span className="text-[10px] font-normal text-slate-400 ml-1">
            {data.generated_at ? `· ${fmtTs(data.generated_at)}` : ""}
          </span>
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={reload} data-testid="telemetry-refresh">
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Row 1 — Queues */}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Queues</p>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <QueueChip label="Incoming" value={q.incoming} accent="blue"
                       testId="telemetry-queue-incoming" />
            <QueueChip label="Escalations" value={q.escalations} accent="amber"
                       testId="telemetry-queue-escalations"
                       breach={sla.escalations?.breached} />
            <QueueChip label="Replay pending" value={q.replay_pending} accent="indigo"
                       testId="telemetry-queue-replay"
                       breach={sla.replays?.breached} />
            <QueueChip label="Dead-letter" value={q.dead_letter} accent="red"
                       testId="telemetry-queue-deadletter"
                       breach={(q.dead_letter || 0) > 0} />
            <QueueChip label="Failed jobs" value={q.failed_jobs} accent="rose"
                       testId="telemetry-queue-failed"
                       breach={sla.failed_jobs?.breached} />
            <QueueChip label="Corrections" value={q.corrections} accent="slate"
                       testId="telemetry-queue-corrections"
                       breach={sla.corrections?.breached} />
          </div>
        </div>

        {/* Row 2 — SLA timers */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] uppercase tracking-wider text-slate-400">
              SLA · per category · elapsed · remaining · breached
            </p>
            <p className="text-[9px] text-slate-400">
              targets:{" "}
              <span className={data.sla?.config_source === "myclaw" ? "text-emerald-600 font-medium" : "text-amber-600"}>
                {data.sla?.config_source === "myclaw" ? "MyClaw config" : "fallback defaults"}
              </span>
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <SLACard slot={sla.escalations} testId="telemetry-sla-escalations" />
            <SLACard slot={sla.failed_jobs} testId="telemetry-sla-failed" />
            <SLACard slot={sla.replays}     testId="telemetry-sla-replays" />
            <SLACard slot={sla.corrections} testId="telemetry-sla-corrections" />
          </div>
        </div>

        {/* Row 3 — Routing accuracy */}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">
            Routing accuracy · last {r.window_days || 7}d
          </p>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <KPI label="Auto-route %" value={`${r.auto_route_pct ?? 0}%`} accent="emerald"
                 testId="telemetry-auto-route" />
            <KPI label="Accuracy %"  value={`${r.accuracy_pct ?? 0}%`}  accent="emerald"
                 testId="telemetry-accuracy" />
            <KPI label="Scope guards" value={r.scope_guard_hits ?? 0}   accent="amber"
                 testId="telemetry-scope-guards" />
            <KPI label="Topic drift"  value={r.topic_drift ?? 0}        accent="orange"
                 testId="telemetry-topic-drift" />
            <KPI label="Low conf."    value={r.low_confidence_escalations ?? 0} accent="rose"
                 testId="telemetry-low-conf" />
            <KPI label="REPORTs"      value={r.report_corrections ?? 0} accent="red"
                 testId="telemetry-reports" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
